import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Paperclip, Smile, X, Reply, FileIcon, Image, Loader2, Sticker } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { TelegramMessage, SendFileData, SendStickerData, SendGifData } from '../types';
import { PasteMediaModal, type SendMode } from './PasteMediaModal';
import { EmojiPicker } from './EmojiPicker';
import { StickerPicker } from './StickerPicker';
import { GifPicker } from './GifPicker';

interface MessageComposerProps {
  onSend: (text: string, replyToMessageId?: number) => void;
  onSendFile?: (data: SendFileData) => void;
  onSendSticker?: (data: SendStickerData) => void;
  onSendGif?: (data: SendGifData) => void;
  replyTo?: TelegramMessage | null;
  onCancelReply?: () => void;
  isLoading?: boolean;
  isUploadingFile?: boolean;
  disabled?: boolean;
}

export function MessageComposer({
  onSend,
  onSendFile,
  onSendSticker,
  onSendGif,
  replyTo,
  onCancelReply,
  isLoading = false,
  isUploadingFile = false,
  disabled = false,
}: MessageComposerProps) {
  const [text, setText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [pastedFile, setPastedFile] = useState<File | null>(null);
  const [isPasteModalOpen, setIsPasteModalOpen] = useState(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [isStickerPickerOpen, setIsStickerPickerOpen] = useState(false);
  const [isGifPickerOpen, setIsGifPickerOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // Обработчик выбора эмодзи — вставляем в позицию курсора
  const handleEmojiSelect = useCallback((emoji: string) => {
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newText = text.slice(0, start) + emoji + text.slice(end);
      setText(newText);
      // Устанавливаем курсор после вставленного эмодзи
      requestAnimationFrame(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
        textarea.focus();
      });
    } else {
      setText(prev => prev + emoji);
    }
  }, [text]);

  // Обработчик выбора стикера
  const handleStickerSelect = useCallback((data: SendStickerData) => {
    if (onSendSticker) {
      onSendSticker({
        ...data,
        replyTo: replyTo?.id,
      });
      onCancelReply?.();
    }
  }, [onSendSticker, replyTo, onCancelReply]);

  // Обработчик выбора GIF
  const handleGifSelect = useCallback((data: SendGifData) => {
    if (onSendGif) {
      onSendGif({
        ...data,
        replyTo: replyTo?.id,
      });
      onCancelReply?.();
    }
  }, [onSendGif, replyTo, onCancelReply]);

  // Focus textarea when reply is set
  useEffect(() => {
    if (replyTo) {
      textareaRef.current?.focus();
    }
  }, [replyTo]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, [text]);

  // Handle paste from clipboard (Ctrl+V / Cmd+V)
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) {return;}

    // Ищем файлы в буфере обмена
    for (const item of items) {
      // Изображения и видео
      if (item.type.startsWith('image/') || item.type.startsWith('video/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          setPastedFile(file);
          setIsPasteModalOpen(true);
        }
        return;
      }
    }

    // Если нет медиа файлов, позволяем стандартную вставку текста
  }, []);

  // Handle file send from paste modal
  const handlePasteModalSend = useCallback((file: File, caption: string, mode: SendMode) => {
    if (onSendFile) {
      onSendFile({
        file,
        caption: caption || undefined,
        replyTo: replyTo?.id,
        forceDocument: mode === 'document',
      });
    }
    setIsPasteModalOpen(false);
    setPastedFile(null);
    onCancelReply?.();
  }, [onSendFile, replyTo, onCancelReply]);

  // Close paste modal
  const handlePasteModalClose = useCallback(() => {
    setIsPasteModalOpen(false);
    setPastedFile(null);
  }, []);

  const handleSend = () => {
    // Если выбран файл, отправляем его
    if (selectedFile && onSendFile) {
      onSendFile({
        file: selectedFile,
        caption: text.trim() || undefined,
        replyTo: replyTo?.id,
      });
      setSelectedFile(null);
      setText('');
      onCancelReply?.();
      // Возвращаем фокус на инпут после отправки
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
      return;
    }

    const trimmedText = text.trim();
    if (!trimmedText || isLoading || disabled) {return;}

    onSend(trimmedText, replyTo?.id);
    setText('');
    onCancelReply?.();
    // Возвращаем фокус на инпут после отправки
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      textareaRef.current?.focus();
    }
    // Сбрасываем input чтобы можно было выбрать тот же файл снова
    e.target.value = '';
  };

  const handleCancelFile = () => {
    setSelectedFile(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Enter отправляет сообщение
    if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
      handleSend();
    }
    // Shift+Enter добавляет новую строку (стандартное поведение)
    // Ctrl+Enter или Cmd+Enter тоже отправляют (для некоторых пользователей)
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t bg-background p-3">
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileSelect}
      />
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* Reply preview */}
      {replyTo && (
        <div className="flex items-center gap-2 mb-2 p-2 bg-muted rounded-lg">
          <Reply className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-primary truncate">
              {replyTo.fromName || 'Сообщение'}
            </p>
            <p className="text-xs text-muted-foreground truncate">
              {replyTo.text || '[Медиа]'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={onCancelReply}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Selected file preview */}
      {selectedFile && (
        <div className="flex items-center gap-2 mb-2 p-2 bg-muted rounded-lg">
          <FileIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium truncate">
              {selectedFile.name}
            </p>
            <p className="text-xs text-muted-foreground">
              {formatFileSize(selectedFile.size)}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={handleCancelFile}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Input area */}
      <div className="flex items-end gap-2">
        {/* Attachment button with dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="flex-shrink-0"
              disabled={disabled || isUploadingFile}
            >
              {isUploadingFile ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Paperclip className="h-5 w-5" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onClick={() => imageInputRef.current?.click()}>
              <Image className="h-4 w-4 mr-2" />
              Фото или видео
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
              <FileIcon className="h-4 w-4 mr-2" />
              Документ
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Text input */}
        <div className="flex-1 relative">
          <Textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={selectedFile ? "Добавить подпись..." : "Написать сообщение..."}
            className="min-h-[40px] max-h-[200px] resize-none pr-24"
            // ponytail: не дизейблим на время отправки (isLoading) — disabled-элемент
            // нельзя сфокусировать, из-за чего фокус терялся после каждого сообщения.
            disabled={disabled || isUploadingFile}
            rows={1}
          />
          {/* Picker buttons */}
          <div className="absolute right-1 bottom-1 flex items-center gap-0.5">
            {/* GIF Picker */}
            {onSendGif && (
              <GifPicker
                open={isGifPickerOpen}
                onOpenChange={setIsGifPickerOpen}
                onGifSelect={handleGifSelect}
                disabled={disabled}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={disabled}
                  title="GIF"
                >
                  <span className="text-xs font-bold">GIF</span>
                </Button>
              </GifPicker>
            )}

            {/* Sticker Picker */}
            {onSendSticker && (
              <StickerPicker
                open={isStickerPickerOpen}
                onOpenChange={setIsStickerPickerOpen}
                onStickerSelect={handleStickerSelect}
                disabled={disabled}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={disabled}
                  title="Стикеры"
                >
                  <Sticker className="h-5 w-5" />
                </Button>
              </StickerPicker>
            )}

            {/* Emoji Picker */}
            <EmojiPicker
              open={isEmojiPickerOpen}
              onOpenChange={setIsEmojiPickerOpen}
              onEmojiSelect={handleEmojiSelect}
              disabled={disabled}
            >
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                disabled={disabled}
                title="Эмодзи"
              >
                <Smile className="h-5 w-5" />
              </Button>
            </EmojiPicker>
          </div>
        </div>

        {/* Send button */}
        <Button
          size="icon"
          className="flex-shrink-0"
          onClick={handleSend}
          disabled={(!text.trim() && !selectedFile) || isLoading || isUploadingFile || disabled}
        >
          {isUploadingFile ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <Send className="h-5 w-5" />
          )}
        </Button>
      </div>

      {/* Paste media modal */}
      <PasteMediaModal
        file={pastedFile}
        open={isPasteModalOpen}
        onClose={handlePasteModalClose}
        onSend={handlePasteModalSend}
        isLoading={isUploadingFile}
      />
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {return `${bytes} Б`;}
  if (bytes < 1024 * 1024) {return `${(bytes / 1024).toFixed(1)} КБ`;}
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}
